import React, { MouseEvent, useRef } from 'react';
import { useIntl, FormattedMessage } from '@kyruus/intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { PMCConfig } from '@kyruus/types';
import Theme from '@kyruus/ui-theme';
import { Button } from '@biome-design-system/ui/Button';
import { TuneIcon } from '@biome-design-system/icons/TuneIcon';

import type {
  GetUpdatedSearch,
  SearchFacet,
  SearchSummary,
  Log
} from 'Src/types';
import { FilterChips } from './FilterChips';
import { getUniqueAppliedFiltersCount, getAllFiltersMessage } from './utils';
import { messages as searchV9Messages } from '../utils';
import messages from './messages';
import { FilterChipWrapper, HorizontalFilterBar } from './styles';
import { TRACKING_EVENTS } from './constants';

export interface FilterBarProps {
  disabled?: boolean;
  facets: SearchFacet[];
  getUpdatedSearch: GetUpdatedSearch;
  searchSummary: SearchSummary;
  config: PMCConfig;
  mapSearchUrl: string;
  onMapButtonClick: (e: MouseEvent) => void;
  log: Log;
  shouldShowMapLink: boolean;
  allFiltersButton: {
    onClick: (e: MouseEvent) => void;
    ariaHasPopup: 'dialog';
    ariaControls?: string;
    ariaExpanded: boolean;
  };
}

export const FilterBar = ({
  disabled,
  facets,
  config,
  getUpdatedSearch,
  searchSummary,
  mapSearchUrl,
  onMapButtonClick,
  log,
  shouldShowMapLink,
  allFiltersButton
}: FilterBarProps) => {
  const intl = useIntl();
  const filtersComponentRef = useRef<HTMLDivElement | null>(null);
  const isScreenMediumOrBelow = useMediaQuery(
    `(max-width:${Theme.screen_medium})`
  );

  const locationChipCount = searchSummary.location ? 1 : 0;

  const countOfAppliedFilters =
    getUniqueAppliedFiltersCount(facets) + locationChipCount;

  const allFiltersLabel = getAllFiltersMessage(countOfAppliedFilters);

  const handleAllFiltersClick = (e: MouseEvent) => {
    allFiltersButton.onClick(e);
    log(TRACKING_EVENTS.ALL_FILTERS_OPEN_DRAWER);
  };

  return (
    <HorizontalFilterBar
      role="toolbar"
      aria-orientation="horizontal"
      aria-label={intl.formatMessage(messages.ariaLabelAllFiltersToolbar)}
    >
      <Button
        aria-controls={
          allFiltersButton.ariaExpanded
            ? allFiltersButton.ariaControls
            : undefined
        }
        aria-expanded={allFiltersButton.ariaExpanded}
        aria-haspopup={allFiltersButton.ariaHasPopup}
        color="neutral"
        data-testid="all-filters-chip"
        disabled={disabled}
        endIcon={<TuneIcon />}
        onClick={handleAllFiltersClick}
        size="small"
        sx={{
          borderWidth: 1,
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
          '&:focus': {
            outline: '3px solid currentcolor',
            outlineColor: '#212121',
            outlineOffset: '-2px'
          }
        }}
        variant="outlined"
      >
        <FormattedMessage
          {...allFiltersLabel}
          values={{ filterCount: countOfAppliedFilters }}
        />
      </Button>
      {isScreenMediumOrBelow || disabled ? null : (
        <FilterChipWrapper
          role="group"
          ref={filtersComponentRef}
          aria-controls="provider-list"
          aria-label={intl.formatMessage(messages.ariaLabelSearchFilters)}
        >
          <FilterChips
            facets={facets}
            getUpdatedSearch={getUpdatedSearch}
            searchSummary={searchSummary}
            config={config}
            log={log}
          />
        </FilterChipWrapper>
      )}
      {shouldShowMapLink && (
        <Button
          aria-label={intl.formatMessage(
            searchV9Messages.mapSearchLinkAriaLabel
          )}
          color="neutral"
          data-testid="MapSearchLink"
          href={mapSearchUrl}
          onClick={onMapButtonClick}
          size="small"
          sx={{
            borderWidth: 1,
            '&:focus': {
              outline: '3px solid currentcolor',
              outlineColor: '#212121',
              outlineOffset: '-2px'
            }
          }}
          variant="outlined"
        >
          <FormattedMessage {...searchV9Messages.mapSearchLinkText} />
        </Button>
      )}
    </HorizontalFilterBar>
  );
};
